import axios, {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios'
import { BASE_URL } from 'configs'
import { HttpStatusCodes } from 'enums/http-status-code'
import { setIsLoading } from 'features/loader/loader'
import { store } from 'store'
import { errorToastManager } from 'utility/errorToastManager'
import { networkRetryManager } from 'utility/networkRetryManager'

declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    hideLoader?: boolean
    suppressErrorToast?: boolean
  }
}

axios.defaults.withCredentials = true

axios.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // No authentication required - app is now anonymous-only
    if (config.url) {
      config.url = BASE_URL + config.url
    }

    if (config.url) {
      if (config.headers && typeof config.headers.set === 'function') {
        config.headers.set(
          'Cache-Control',
          'no-cache, no-store, must-revalidate, post-check=0, pre-check=0'
        )
        config.headers.set('Pragma', 'no-cache')
        config.headers.set('Expires', '0')
      } else if (config.headers) {
        config.headers['Cache-Control'] =
          'no-cache, no-store, must-revalidate, post-check=0, pre-check=0'
        config.headers.Pragma = 'no-cache'
        config.headers.Expires = '0'
      }
    }

    if (!config.hideLoader) {
      // showLoader()
      store.dispatch(setIsLoading(true))
    }
    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  }
)

axios.interceptors.response.use(
  (response: AxiosResponse) => {
    // hideLoader()
    store.dispatch(setIsLoading(false))
    return response
  },
  (error: AxiosError) => {
    // Stop loading state
    store.dispatch(setIsLoading(false))

    // Store failed request for automatic retry on network recovery
    if (error.config) {
      networkRetryManager.storeFailedRequest(error.config, error)
    }

    // Only show toast if not suppressed
    if (!error.config?.suppressErrorToast) {
      // Use intelligent error handling
      errorToastManager.handleError(
        error,
        error.response?.status,
        `API: ${error.config?.url || 'Unknown endpoint'}`
      )
    }

    // Handle specific error cases
    switch (error.response?.status) {
      case HttpStatusCodes.Forbidden:
        setTimeout(
          // eslint-disable-next-line no-return-assign
          () =>
            (window.location.href =
              // eslint-disable-next-line prefer-template
              window.location.protocol + '//' + window.location.host),
          5000
        )
        break
      case HttpStatusCodes.InternalServerError:
      case HttpStatusCodes.ServiceUnavailable:
        break
      default:
    }

    return Promise.reject(error)
  }
)

// Use your custom config type everywhere
// (InternalAxiosRequestConfig is already extended to include hideLoader)
type CustomAxiosConfig = Partial<InternalAxiosRequestConfig>

const httpClient = {
  get: <T = any>(url: string, config?: CustomAxiosConfig) =>
    axios.get<T>(url, config),
  post: <T = any>(url: string, data?: any, config?: CustomAxiosConfig) =>
    axios.post<T>(url, data, config),
  put: <T = any>(url: string, data?: any, config?: CustomAxiosConfig) =>
    axios.put<T>(url, data, config),
  delete: <T = any>(url: string, config?: CustomAxiosConfig) =>
    axios.delete<T>(url, config),
  patch: <T = any>(url: string, data?: any, config?: CustomAxiosConfig) =>
    axios.patch<T>(url, data, config),
}

export default httpClient
